var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as L from 'leaflet';
import identity from 'lodash/identity';
import chroma from 'chroma-js';
import queryString from 'query-string';
import axios from 'axios';
import { addClassNames, createElement } from './lib/utils/dom';
/**
 * Color overflow defined for a bunch of GridAPI WMTS services.
 */
const colorsOverflow = {
    luft_bafu_hourly_o3: { over: true, under: false },
    luft_bafu_hourly_pm10: { over: true, under: false },
    luft_bafu_hourly_no2: { over: true, under: false },
    luft_bafu_yearly_pm10: { over: true, under: false },
    luft_bafu_yearly_no2: { over: true, under: false },
    luft_bafu_yearly_so2: { over: true, under: false },
    luft_bafu_yearly_o3: { over: true, under: false },
    luft_bafu_yearly_aot40f: { over: true, under: false },
    luft_bafu_yearly_nh3: { over: true, under: false },
    luft_bafu_yearly_ndep: { over: true, under: false },
    luft_bafu_yearly_nexed: { over: true, under: true },
    luft_bafu_daily_o3: { over: true, under: false },
    luft_bafu_daily_pm10: { over: true, under: false },
    luft_bafu_daily_no2: { over: true, under: false },
    radar_colored_midres: { over: true, under: false },
    wrfd03_wind10_flugbasis: { over: true, under: false },
    wrfd03_wind850hpa_flugbasis: { over: true, under: false },
    wrfd03_wind800hpa_flugbasis: { over: true, under: false },
    wrfd03_wind700hpa_flugbasis: { over: true, under: false },
    gfs_wind10: { over: true, under: false },
};
/**
 * Determines if a color should overflow.
 * @param service - The GridAPI WMTS service.
 * @param direction - The overflow direction.
 * @returns Whether or not the colors should overflow.
 * @see {@link https://gridapi.meteotest.ch/map_services|GridAPI WMTS Services}
 */
function hasColorOverflow(service, direction) {
    return (colorsOverflow[service] !== undefined &&
        colorsOverflow[service][direction] !== undefined &&
        colorsOverflow[service][direction]);
}
/**
 * Converts an 8-bit value to a fraction of 1.
 * This is needed as the service returns all values as 0-255,
 * while the correct format is between 0 and 1.
 * @param byte - The 8-bit value to convert.
 * @returns The converted 8-bit value.
 * @see {@link https://gitlab.mt.local/web_software/maap/blob/master/src/controls/colorLegendController.js#L176|Original source from M.A.A.P. codebase}
 */
function convertAlpha(byte) {
    byte = byte || 255;
    return Math.round((byte * 10) / 255) / 10;
}
/**
 * The colorstep legend control's default options.
 */
const CONTROL_LEGEND_COLORSTEP_OPTIONS_DEFAULT = {
    position: 'topleft',
    formatter: identity,
    baseUri: 'https://gridapi.meteotest.ch',
};
/**
 * The colorstep legend control.
 */
export class ControlLegendColorstep extends L.Control {
    /**
     * @param options - The colorstep legend options.
     */
    constructor(options) {
        super(Object.assign(Object.assign({}, CONTROL_LEGEND_COLORSTEP_OPTIONS_DEFAULT), options));
    }
    /**
     * The hook to execute when the control is added to the map.
     * @returns The control's container element.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onAdd() {
        const container = L.DomUtil.create('div', 'maap_p-control-legend-colorstep');
        if (this.options.baseUri && this.options.wmtsName) {
            addClassNames(container, [
                'maap_c-floating',
                'maap_c-control',
                'is-loading',
            ]);
            this.fetch();
        }
        return container;
    }
    /**
     * The hook to execute when the control is removed from the map.
     * @see {@link https://leafletjs.com/reference-1.7.1.html#control-extension-methods}
     */
    onRemove() {
        // do nothing
    }
    /**
     * Fetch the control's data.
     */
    fetch() {
        return __awaiter(this, void 0, void 0, function* () {
            const params = {
                format: 'json',
            };
            if (this.options.unitKey) {
                params.unit = this.options.unitKey;
            }
            let uri = `${this.options.baseUri}/wmts/${this.options.wmtsName}/legend`;
            uri += '?' + queryString.stringify(params);
            try {
                const res = yield axios.get(uri);
                this.handleResponse(res.data);
            }
            catch (err) {
                this.handleError(err.response.statusText || err.response.status);
            }
        });
    }
    /**
     * Use the data received via the API response to render the control.
     * @param json - The response JSON.
     */
    handleResponse(json) {
        const { colors, levels } = json.legend;
        let color_under = json.legend.color_under;
        let color_over = json.legend.color_over;
        const { unit } = json.metadata;
        const domain = [levels[0], levels[levels.length - 1]];
        const boxes = [];
        const ticks = [];
        if (!hasColorOverflow(this.options.wmtsName, 'under')) {
            color_under = null;
        }
        if (!hasColorOverflow(this.options.wmtsName, 'over')) {
            color_over = null;
        }
        if (color_under) {
            boxes.push({
                index: boxes.length,
                text: `< ${domain[0]} ${unit}`,
                color: color_under,
            });
        }
        for (let i = 0; i < levels.length - 1; i++) {
            const n1 = levels[i];
            const n2 = levels[i + 1];
            const index = boxes.length;
            boxes.push({
                index,
                text: `${n1} - ≤${n2} ${unit}`,
                color: colors[i],
            });
            if (this.options.interval && i % this.options.interval === 0) {
                ticks.push({
                    index,
                    text: this.options.formatter(n1),
                });
            }
        }
        ticks.push({
            index: boxes.length,
            text: this.options.formatter(domain[1]),
        });
        if (color_over) {
            boxes.push({
                index: boxes.length,
                text: `> ${domain[1]} ${unit}`,
                color: color_over,
            });
        }
        const size = 100 / boxes.length;
        const data = {
            unit,
            ranges: boxes.map((box) => ({
                text: box.text,
                color: chroma(box.color[0], box.color[1], box.color[2], convertAlpha(box.color[3])).css(),
                size,
                offset: 100 - size * box.index - size,
            })),
            labels: ticks.map((label) => ({
                text: label.text,
                offset: 100 - size * label.index,
            })),
        };
        if (this.options.title) {
            data.title = this.options.title;
        }
        if (this.options.horizontal) {
            if (this._container) {
                this._container.classList.add('maap_p-control-legend-colorstep--horizontal');
            }
            data.horizontal = true;
        }
        if (this._container) {
            this._container.innerHTML = this.render(data);
            L.DomUtil.removeClass(this._container, 'is-loading');
        }
    }
    /**
     * Handle a error produced by an unsuccessfull API call.
     * @param error-  The thrown error to be handled.
     */
    handleError(error) {
        console.error(error);
        if (this._container) {
            L.DomUtil.removeClass(this._container, 'is-loading');
        }
    }
    /**
     * Render the DOM according to the provided data.
     * @param data - The render data.
     * @returns The rendered HTML as a string;
     */
    render(data) {
        const elements = [];
        if (data.title) {
            elements.push(createElement({ tagName: 'strong', innerHTML: data.title }));
        }
        if (data.unit) {
            elements.push(createElement({
                tagName: 'p',
                classNames: ['maap_p-control-legend-colorstep__unit'],
                innerHTML: data.unit,
            }));
        }
        if (data.ranges.length > 0 || data.labels.length > 0) {
            const elLegend = createElement({
                tagName: 'div',
                classNames: ['maap_p-control-legend-colorstep__legend'],
            });
            const elSteps = createElement({
                tagName: 'div',
                classNames: ['maap_p-control-legend-colorstep__steps'],
            });
            if (data.ranges.length > 0) {
                elSteps.appendChild(this.createElRanges(data.ranges, data.horizontal));
            }
            if (data.labels.length > 0) {
                elSteps.appendChild(this.createElLabels(data.labels, data.horizontal));
            }
            elLegend.appendChild(elSteps);
            elements.push(elLegend);
        }
        return elements.map((el) => el.outerHTML).join('');
    }
    /**
     * Create the ranges DOM element.
     * @param ranges - The ranges data.
     * @param isHorizontal - Whether the colorlegend is horizontal or not.
     * @returns The ranges DOM element.
     */
    createElRanges(ranges, isHorizontal) {
        const elRanges = createElement({
            tagName: 'div',
            classNames: ['maap_p-control-legend-colorstep__ranges'],
        });
        ranges.map((range) => {
            const elRange = createElement({
                tagName: 'div',
                classNames: ['maap_p-control-legend-colorstep__range'],
            });
            if (isHorizontal) {
                elRange.style.left = `${range.offset.toString()}%`;
                elRange.style.width = `${range.size.toString()}%`;
            }
            else {
                elRange.style.top = `${range.offset.toString()}%`;
                elRange.style.height = `${range.size.toString()}%`;
            }
            elRange.style.backgroundColor = range.color;
            elRange.title = range.text;
            elRanges.appendChild(elRange);
        });
        return elRanges;
    }
    /**
     * Create the labels DOM element.
     * @param labels - The labels data.
     * @param isHorizontal - Whether the colorlegend is horizontal or not.
     * @returns The labels DOM element.
     */
    createElLabels(labels, isHorizontal) {
        const elLabels = createElement({
            tagName: 'div',
            classNames: ['maap_p-control-legend-colorstep__labels'],
        });
        labels.map((label) => {
            const elLabel = createElement({
                tagName: 'div',
                classNames: ['maap_p-control-legend-colorstep__label'],
                innerHTML: label.text,
            });
            if (isHorizontal) {
                elLabel.style.left = `${label.offset.toString()}%`;
            }
            else {
                elLabel.style.top = `${label.offset.toString()}%`;
            }
            elLabels.appendChild(elLabel);
        });
        return elLabels;
    }
}
//# sourceMappingURL=ControlLegendColorstep.js.map